import { useState } from 'react';
import type { Finding, RecipeMetrics } from '~/lib/network/funnel-report';

interface FindingCardProps {
  finding: Finding;
  recipeName: string;
  metrics: RecipeMetrics;
  onViewRawData: (metrics: RecipeMetrics) => void;
}

const SEVERITY_STYLES = {
  positive: {
    dot: 'bg-success',
    badge: 'badge-success',
  },
  warning: {
    dot: 'bg-warning',
    badge: 'badge-warning',
  },
  critical: {
    dot: 'bg-error',
    badge: 'badge-error',
  },
};

export function FindingCard({
  finding,
  recipeName: _recipeName,
  metrics,
  onViewRawData,
}: FindingCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const styles = SEVERITY_STYLES[finding.severity];

  return (
    <div
      className={`collapse bg-base-100 border border-base-300 rounded-box ${
        isExpanded ? 'collapse-open' : ''
      }`}
    >
      <input
        type="checkbox"
        checked={isExpanded}
        onChange={(e) => setIsExpanded(e.target.checked)}
        className="min-h-0"
      />

      {/* Collapsed Header */}
      <div className="collapse-title min-h-0 py-4 pr-12">
        <div className="flex items-start gap-3">
          <span className={`w-3 h-3 rounded-full ${styles.dot} mt-1 shrink-0`} />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-base-content leading-tight">
              {finding.headline}
            </p>
            <p className="text-sm text-base-content/60 mt-1">
              {finding.subline}
            </p>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      <div className="collapse-content">
        <div className="pt-2 border-t border-base-200">
          {/* What's happening */}
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-base-content/80 mb-2">
              What's happening
            </h4>
            <p className="text-sm text-base-content/70 whitespace-pre-line">
              {finding.what_happening}
            </p>
          </div>

          {/* Why it matters */}
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-base-content/80 mb-2">
              Why it matters
            </h4>
            <p className="text-sm text-base-content/70 whitespace-pre-line">
              {finding.why_matters}
            </p>
          </div>

          {/* What to do */}
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-base-content/80 mb-2">
              What to do
            </h4>
            <p className="text-sm text-base-content/70 whitespace-pre-line">
              {finding.what_to_do}
            </p>
          </div>

          {/* See raw data link */}
          <button
            className="btn btn-ghost btn-sm mt-4 text-base-content/60 hover:text-base-content gap-1"
            onClick={(e) => {
              e.stopPropagation();
              onViewRawData(metrics);
            }}
          >
            See raw data
            <span className="text-xs">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}
